/**
 * @fileoverview Shared "date calc" controls for precedent templates
 * @module components/settings/precedents/date-calc-controls
 *
 * Purpose:
 * - One set of amount / unit / direction / base-field controls, used by both
 *   the PDF field editor (placements on a form) and the letter editor's field
 *   chip bubble menu, so the two surfaces can never drift apart.
 *
 * A date calc derives a date field's value from another date field at
 * generation time — e.g. the ROCAP records period "From" as 5 years before
 * the Date of Appointment. It is keyed by FIELD, never by placement: a letter
 * may reference the same date in several paragraphs and a PDF date is often
 * split across day / month / year boxes, and all of them must show the same
 * derived value. An explicit value entered during generation still wins.
 *
 * Used by:
 * - src/components/settings/precedents/PdfFieldEditor.tsx
 * - src/ui/composed/editor/template-editor.tsx
 */

import { Input } from "@/ui/primitives/input";
import { cn } from "@/utils/misc";
import {
  DATE_CALC_DIRECTIONS,
  DATE_CALC_UNITS,
  type DateCalc,
} from "../../../../convex/settings/precedents/dateCalc";
import {
  buildDateCalcBaseOptions,
  type DateCalcCustomField,
} from "./date-calc-utils";

const SELECT_CLASS =
  "h-6 rounded border border-border bg-background px-1 text-[10px] text-foreground focus:outline-none focus:ring-1 focus:ring-ring";

/**
 * The calc's editable controls: "[5] [years] [before] / [Date of Appointment]".
 * Presentation-only — hosts supply their own panel chrome, heading and
 * remove affordance.
 */
export function DateCalcFields({
  calc,
  ownKey,
  customFields,
  onChange,
  className,
}: {
  calc: DateCalc;
  /** The field being calculated — excluded from the base-field options. */
  ownKey: string;
  customFields: DateCalcCustomField[];
  onChange: (calc: DateCalc) => void;
  className?: string;
}) {
  const baseGroups = buildDateCalcBaseOptions(ownKey, customFields);

  return (
    <div className={cn("space-y-1", className)}>
      <div className="flex items-center gap-1">
        <Input
          type="number"
          min={0}
          value={Number.isFinite(calc.amount) ? calc.amount : ""}
          className="h-6 w-10 shrink-0 px-1 text-center text-[10px]"
          onChange={(e) => {
            const n = Math.max(0, Math.floor(Number(e.target.value)));
            onChange({ ...calc, amount: Number.isFinite(n) ? n : 0 });
          }}
        />
        <select
          className={cn(SELECT_CLASS, "min-w-0 flex-1")}
          value={calc.unit}
          onChange={(e) =>
            onChange({ ...calc, unit: e.target.value as DateCalc["unit"] })
          }
        >
          {DATE_CALC_UNITS.map((u) => (
            <option key={u.value} value={u.value}>
              {u.label}
            </option>
          ))}
        </select>
        <select
          className={cn(SELECT_CLASS, "shrink-0")}
          value={calc.direction}
          onChange={(e) =>
            onChange({
              ...calc,
              direction: e.target.value as DateCalc["direction"],
            })
          }
        >
          {DATE_CALC_DIRECTIONS.map((d) => (
            <option key={d.value} value={d.value}>
              {d.label}
            </option>
          ))}
        </select>
      </div>
      <select
        className={cn(SELECT_CLASS, "w-full")}
        value={calc.baseKey}
        onChange={(e) => onChange({ ...calc, baseKey: e.target.value })}
      >
        {baseGroups.map((g) => (
          <optgroup key={g.group} label={g.group}>
            {g.options.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </div>
  );
}
